import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const mutationConfig = { headers: { 'x-training-request': '1' }, withCredentials: true };

function createSubmissionId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function Quiz({ userData, onComplete }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [reloadRequired, setReloadRequired] = useState(false);
  const startedRef = useRef(false);
  const pendingSubmissionRef = useRef(null);
  const completedRef = useRef(false);
  const resultRef = useRef(null);

  const loadQuiz = useCallback(async () => {
    setLoading(true);
    setError('');
    setReloadRequired(false);
    try {
      const { data } = await axios.post('/api/quiz/start', {}, mutationConfig);
      setQuiz(data.quiz || data);
      setAnswers({});
      setResult(null);
      setCooldownSeconds(0);
      pendingSubmissionRef.current = null;
      completedRef.current = false;
    } catch (requestError) {
      setQuiz(null);
      setError(requestError.response?.data?.error || 'The assigned knowledge check could not be started. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    loadQuiz();
  }, [loadQuiz]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldownSeconds(value => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (!result || !resultRef.current) return;
    resultRef.current.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    resultRef.current.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [result]);

  const orderedAnswers = useMemo(() => (
    quiz?.questions?.map(item => ({ questionId: item.id, optionId: answers[item.id] })) || []
  ), [answers, quiz]);
  const answeredCount = orderedAnswers.filter(answer => Boolean(answer.optionId)).length;
  const totalQuestions = quiz?.questions?.length || 0;
  const allAnswered = totalQuestions > 0 && answeredCount === totalQuestions;

  const selectAnswer = (questionId, optionId) => {
    if (submitting || result?.passed) return;
    setAnswers(current => ({ ...current, [questionId]: optionId }));
    setResult(null);
    setError('');
    pendingSubmissionRef.current = null;
  };

  const continueToAcknowledgment = async () => {
    if (continuing || completedRef.current) return;
    setContinuing(true);
    setError('');
    try {
      await onComplete();
      completedRef.current = true;
    } catch (_) {
      setError('Your passing score is safely recorded, but the next page did not load. Select Continue to Acknowledgment to try again.');
    } finally {
      setContinuing(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!quiz || !allAnswered || submitting || cooldownSeconds > 0 || result?.passed) return;

    const answerSignature = JSON.stringify(orderedAnswers);
    if (!pendingSubmissionRef.current || pendingSubmissionRef.current.answerSignature !== answerSignature) {
      pendingSubmissionRef.current = {
        submissionId: createSubmissionId(),
        answerSignature,
      };
    }

    setSubmitting(true);
    setError('');
    let savedResult = null;
    try {
      const { data } = await axios.post('/api/quiz/submit', {
        submissionId: pendingSubmissionRef.current.submissionId,
        quizVersion: quiz.version,
        answers: orderedAnswers,
      }, mutationConfig);
      savedResult = data.quiz || data;
      setResult(savedResult);
    } catch (requestError) {
      const status = requestError.response?.status;
      const details = requestError.response?.data;
      if (status === 422 && details?.quiz) {
        setResult(details.quiz);
        setCooldownSeconds(Number(details.quiz.retryAfterSeconds) || 0);
        pendingSubmissionRef.current = null;
      } else {
        if (status && status < 500) pendingSubmissionRef.current = null;
        if (status === 429) setCooldownSeconds(Number(details?.retryAfterSeconds) || 1);
        if (details?.code === 'QUIZ_VERSION_STALE') setReloadRequired(true);
        setError(details?.error || 'The secure grading service could not confirm your submission. Your answers are still here; try again.');
      }
    } finally {
      setSubmitting(false);
    }
    if (savedResult?.passed) await continueToAcknowledgment();
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="card quiz-loading"><div className="spinner" /><p>Preparing your secure knowledge check…</p></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="page-container">
        <div className="card quiz-unavailable">
          <h2>Knowledge check unavailable</h2>
          <p>{error || 'No approved knowledge check is configured for this role.'}</p>
          <button type="button" className="btn btn-primary quiz-retry" onClick={loadQuiz}>Try Again</button>
        </div>
      </div>
    );
  }

  const scorePercent = result?.attempt?.scorePercent;

  return (
    <div className="page-container">
      <div className="quiz-heading">
        <div className="step-badge"><span className="step-badge-dot" />Step 3 of 5 — Secure Knowledge Check</div>
        <h1 className="page-title">{quiz.title}</h1>
        <p className="page-subtitle">
          Answer all {totalQuestions} questions for <strong>{userData.role}</strong>. You need <strong>{quiz.passingPercent}%</strong> to pass. Your identity and score are recorded by the portal automatically—no Google sign-in is needed.
        </p>
      </div>

      <div className="quiz-summary" aria-live="polite">
        <div>
          <span className="quiz-summary-label">Progress</span>
          <strong>{answeredCount} of {totalQuestions} answered</strong>
        </div>
        <div className="quiz-summary-track" aria-hidden="true">
          <span style={{ width: `${totalQuestions ? (answeredCount / totalQuestions) * 100 : 0}%` }} />
        </div>
      </div>

      {result && !result.passed && (
        <div className="quiz-result quiz-result--failed" role="status" ref={resultRef} tabIndex={-1}>
          <span className="quiz-result-icon" aria-hidden="true">!</span>
          <div>
            <h2>Review and try again</h2>
            <p>You scored <strong>{scorePercent}%</strong>; {result.attempt?.passingPercent || quiz.passingPercent}% is required. Your incorrect answers are not revealed—review the training, adjust your choices, and resubmit.</p>
          </div>
        </div>
      )}

      {result?.passed && (
        <div className="quiz-result quiz-result--passed" role="status" ref={resultRef} tabIndex={-1}>
          <span className="quiz-result-icon" aria-hidden="true">✓</span>
          <div>
            <h2>Knowledge check passed</h2>
            <p>Your verified score was {scorePercent}%. Your result is saved even if the next page needs to be retried.</p>
            <button type="button" className="btn btn-primary quiz-continue" onClick={continueToAcknowledgment} disabled={continuing || completedRef.current}>
              {continuing ? 'Opening acknowledgment…' : 'Continue to Acknowledgment →'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="quiz-error" role="alert">
          <span>{error}</span>
          {reloadRequired && <button type="button" className="btn btn-primary" onClick={loadQuiz}>Reload Knowledge Check</button>}
        </div>
      )}

      <form onSubmit={submit} className="quiz-form">
        {quiz.questions.map((item, questionIndex) => (
          <fieldset className="quiz-question" key={item.id} disabled={submitting || result?.passed}>
            <legend>
              <span className="quiz-question-number">{questionIndex + 1}</span>
              <span>{item.prompt}</span>
            </legend>
            <div className="quiz-options">
              {item.options.map(option => {
                const selected = answers[item.id] === option.id;
                return (
                  <label className={`quiz-option${selected ? ' quiz-option--selected' : ''}`} key={option.id}>
                    <input
                      type="radio"
                      name={item.id}
                      value={option.id}
                      checked={selected}
                      onChange={() => selectAnswer(item.id, option.id)}
                    />
                    <span className="quiz-option-marker" aria-hidden="true" />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}

        <div className="quiz-actions">
          <span>{allAnswered ? 'All questions answered.' : `${totalQuestions - answeredCount} remaining.`}</span>
          <button
            type="submit"
            className="btn btn-primary btn--lg"
            disabled={!allAnswered || submitting || cooldownSeconds > 0 || result?.passed}
          >
            {submitting
              ? 'Grading securely…'
              : result?.passed
                ? 'Passed ✓'
                : cooldownSeconds > 0
                  ? `Try again in ${cooldownSeconds}s`
                  : result
                    ? 'Resubmit Answers →'
                    : 'Submit Knowledge Check →'}
          </button>
        </div>
      </form>
    </div>
  );
}
